import { SortDescriptor } from '@progress/kendo-data-query';

/**
 * Returns true if the field name looks like a date/time field.
 *
 * Two detection strategies are combined:
 *
 * 1. The field name contains 'date' or 'time' (case-insensitive), e.g.
 *    createdDate, arrivalDate, timeIn, deliveryTime, operationCompletedTime.
 *
 * 2. The last camelCase "word" of the field (or last dot-notation segment) is a
 *    known maritime date abbreviation: ETA, ETD, ATA, ATD (case-insensitive).
 *    Examples: eta, etd, ata, atd, portOfCallETA, portOfCallEtd, vesselVisit.ata.
 *    Using last-word extraction avoids false positives like 'data' → 'ata'.
 */
export function isDateField(field: string): boolean {
    if (/date|time/i.test(field)) {
        return true;
    }

    // Extract last dot-notation segment, then split into camelCase words and take the last one.
    // Regex splits: uppercase runs before another uppercase+lower ('ETA'), PascalCase ('Call'),
    // plain lowercase ('port'), or digits.
    const lastSegment = field.split('.').pop() ?? field;
    const words = lastSegment.match(/[A-Z]+(?=[A-Z][a-z]|\d|$)|[A-Z]?[a-z]+|\d+|[A-Z]+/g) ?? [];
    const lastWord = words[words.length - 1] ?? lastSegment;

    return /^(eta|etd|ata|atd)$/i.test(lastWord);
}

/**
 * Adjusts sort descriptors so that date/time fields default to DESC on the first
 * click instead of Kendo's default ASC, producing the reversed cycle:
 *
 *   none → DESC → ASC → none → DESC → …
 *
 * Non-date fields are left unchanged (normal Kendo cycle: none → ASC → DESC → none).
 *
 * @param newSort    The SortDescriptor[] emitted by the Kendo Grid's (sortChange) event.
 * @param prevSort   The SortDescriptor[] that was previously bound to the grid ([sort] input).
 * @param isDateFn   Optional custom predicate to determine whether a field is a date field.
 *                   Defaults to the built-in `isDateField`. Base class components can pass
 *                   their own `isDateSortField` override to handle non-standard field names.
 */
export function applyDateDefaultSort(
    newSort: SortDescriptor[],
    prevSort: SortDescriptor[],
    isDateFn: (field: string) => boolean = isDateField
): SortDescriptor[] {
    let result: SortDescriptor[] = [...newSort];

    for (const prev of prevSort) {
        if (!isDateFn(prev.field)) {
            continue;
        }

        const next = result.find((s) => s.field === prev.field);

        if (!prev.dir) {
            // Field was not sorted before; Kendo emits 'asc' — convert to 'desc' (Rule 1).
            if (next && next.dir === 'asc') {
                next.dir = 'desc';
            }
        } else if (prev.dir === 'desc') {
            // Field was DESC; Kendo clears the dir on allowUnsort click.
            // Inject ASC instead of going straight to unsorted (Rule 2).
            // Only apply when the field is still present in newSort (user clicked the date column
            // itself); do not re-inject when the user switched to a different column, as that would
            // produce a multi-entry sortsArr and trigger Kendo's sort-order number display.
            if (next && !next.dir) {
                next.dir = 'asc';
            }
        } else if (prev.dir === 'asc') {
            // Field was ASC; Kendo emits 'desc' — this is the "complete the cycle" step, so
            // remove the descriptor to produce the unsorted state (Rule 3).
            if (next && next.dir === 'desc') {
                result = result.filter((s) => s.field !== prev.field);
            }
        }
    }

    // Handle the case where a date field appears in newSort but was NOT in prevSort at all
    // (brand-new sort on a field the grid hasn't seen before). Kendo emits 'asc' — flip to 'desc'.
    for (const s of result) {
        if (isDateFn(s.field) && s.dir === 'asc') {
            const wasPreviouslySorted = prevSort.some((p) => p.field === s.field);
            if (!wasPreviouslySorted) {
                s.dir = 'desc';
            }
        }
    }

    return result;
}
